import { useParams, Link } from 'react-router-dom';
import { getMovieById, movies } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id || '');

  if (!movie) {
    return <NotFound />;
  }

  const relatedMovies = movies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 6);

  const movieSchema = {
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    dateCreated: `${movie.year}-01-01`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      ratingCount: Math.floor(Math.random() * 100000) + 10000,
    },
  };

  const articleSchema = {
    '@type': 'Article',
    headline: `${movie.title} - 星空电影网电影详情`,
    description: movie.description,
    datePublished: movie.releaseDate,
    author: {
      '@type': 'Organization',
      name: '星空电影网',
    },
    publisher: {
      '@type': 'Organization',
      name: '星空电影网',
    },
  };

  return (
    <>
      <SEOHead
        title={`${movie.title} - 星空电影网高清在线观看`}
        description={`${movie.title}：${movie.description} 导演${movie.director}，主演${movie.cast.slice(0, 3).join('、')}等。星空电影网高清免费在线观看。`}
        keywords={`${movie.title},${movie.title}在线观看,${movie.title}高清,${movie.category},星空电影网`}
        canonicalUrl={`https://www.xingkongmovie.com/movie/${movie.id}`}
        ogType="article"
        ogImage={movie.imageUrl}
        schemaType="Movie"
        schemaData={movieSchema}
      />

      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: movie.category, path: `/category/${movie.categorySlug}` },
              { name: movie.title },
            ]}
          />
        </div>

        <article className="max-w-[1400px] mx-auto px-4 md:px-6">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(articleSchema),
            }}
          />

          <div className="flex flex-col lg:flex-row gap-8 py-6">
            <div className="flex-shrink-0">
              <div className="w-full sm:w-64 lg:w-72 aspect-[2/3] rounded-lg overflow-hidden">
                <img
                  src={movie.imageUrl}
                  alt={movie.title}
                  title={`${movie.title} - 星空电影网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {movie.isNew && (
                  <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500 text-white">
                    最新
                  </span>
                )}
                {movie.isHot && (
                  <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-primary-500 text-background-950">
                    热门
                  </span>
                )}
                <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-background-200 text-foreground-400">
                  {movie.category}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100 mb-4">
                {movie.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mb-5">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-primary-500 text-lg"></i>
                  <span className="text-xl font-bold text-primary-400">{movie.rating}</span>
                  <span className="text-sm text-foreground-500">/ 10</span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 mb-6 text-sm">
                <div>
                  <span className="text-foreground-500">上映年份：</span>
                  <span className="text-foreground-200">{movie.year}年</span>
                </div>
                <div>
                  <span className="text-foreground-500">片长：</span>
                  <span className="text-foreground-200">{movie.duration}</span>
                </div>
                <div>
                  <span className="text-foreground-500">地区：</span>
                  <span className="text-foreground-200">{movie.region}</span>
                </div>
                <div>
                  <span className="text-foreground-500">语言：</span>
                  <span className="text-foreground-200">{movie.language}</span>
                </div>
                <div>
                  <span className="text-foreground-500">上映日期：</span>
                  <span className="text-foreground-200">{movie.releaseDate}</span>
                </div>
                <div>
                  <span className="text-foreground-500">导演：</span>
                  <span className="text-foreground-200">{movie.director}</span>
                </div>
              </div>

              <div className="mb-5">
                <h4 className="text-sm font-medium text-foreground-400 mb-2">主演</h4>
                <div className="flex flex-wrap gap-2">
                  {movie.cast.map((actor) => (
                    <span
                      key={actor}
                      className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-300 border border-background-200/50"
                    >
                      {actor}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-line text-lg"></i>
                  立即观看
                </button>
                <button className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-foreground-300/30 text-foreground-200 text-sm font-medium hover:bg-foreground-100/10 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
                <button className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-foreground-300/30 text-foreground-200 text-sm font-medium hover:bg-foreground-100/10 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>

          <div className="py-6 border-t border-background-200/50">
            <h2 className="font-heading text-xl font-bold text-foreground-100 mb-4">
              剧情简介
            </h2>
            <p className="text-sm text-foreground-400 leading-relaxed max-w-4xl">
              {movie.fullDescription}
            </p>
          </div>
        </article>

        {relatedMovies.length > 0 && (
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 border-t border-background-200/50">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-bold text-foreground-100">
                相关推荐
              </h2>
              <Link
                to={`/category/${movie.categorySlug}`}
                className="text-sm text-primary-400 hover:text-primary-300 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                更多{movie.category}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {relatedMovies.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}